import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ContentRow } from '../../../types/homepage';

interface HeroCarouselProps {
    row: ContentRow;
    onVideoClick?: (videoId: string) => void;
    theme?: {
        primaryColor?: string;
        buttonColor?: string;
        buttonTextColor?: string;
        fontFamily?: string;
        overlayStrength?: 'light' | 'medium' | 'heavy';
    };
}

function formatViews(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n.toString();
}

const OVERLAY = {
    light: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 55%, transparent 100%)',
    medium: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 50%, transparent 100%)',
    heavy: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.1) 100%)',
};

const HeroCarousel: React.FC<HeroCarouselProps> = ({ row, onVideoClick, theme = {} }) => {
    const {
        primaryColor = '#7c3aed',
        buttonColor = '#ffffff',
        buttonTextColor = '#111827',
        fontFamily = "'DM Sans', sans-serif",
        overlayStrength = 'medium',
    } = theme;

    const { content = [], settings = {}, layoutStyle = 'default' } = row;

    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [progress, setProgress] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const AUTOPLAY_MS = 6000;
    const PROGRESS_TICK = 60;

    const clearTimers = useCallback(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (progressRef.current) clearInterval(progressRef.current);
    }, []);

    const startTimers = useCallback(() => {
        if (!settings?.autoPlay || content.length <= 1 || isPaused) return;
        clearTimers();
        setProgress(0);

        const ticks = AUTOPLAY_MS / PROGRESS_TICK;
        let tick = 0;
        progressRef.current = setInterval(() => {
            tick++;
            setProgress(Math.min((tick / ticks) * 100, 100));
        }, PROGRESS_TICK);

        intervalRef.current = setInterval(() => {
            goTo('next');

        }, AUTOPLAY_MS);
    }, [settings?.autoPlay, content.length, isPaused]);

    useEffect(() => {
        startTimers();
        return clearTimers;

    }, [startTimers, clearTimers, currentIndex]);

    const goTo = useCallback(
        (dir: 'next' | 'prev' | number) => {
            if (isAnimating) return;
            setIsAnimating(true);

            if (typeof dir === 'number') {
                setCurrentIndex(dir);
            } else {
                setCurrentIndex((prev) =>
                    dir === 'next'
                        ? (prev + 1) % content.length
                        : (prev - 1 + content.length) % content.length
                );
            }

            setTimeout(() => setIsAnimating(false), 600);
        },
        [isAnimating, content.length]
    );

    if (!content || content.length === 0) return null;

    const current = content[currentIndex];
    const isCinematic = layoutStyle === 'cinematic';

    return (
        <>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500&family=Syne:wght@600;700;800&display=swap');

        .hc-root {
          --hc-primary: ${primaryColor};
          --hc-btn-bg: ${buttonColor};
          --hc-btn-text: ${buttonTextColor};
          --hc-font-body: ${fontFamily};
          --hc-font-display: 'Syne', sans-serif;
        }
        .hc-wrap { position: relative; width: 100%; height: clamp(440px, 62vw, 680px); overflow: hidden; background: #0a0a0f; font-family: var(--hc-font-body); }
        .hc-slide { position: absolute; inset: 0; background-size: cover; background-position: center top; transition: opacity 0.65s ease, transform 0.65s ease; }
        .hc-slide.entering-next { opacity: 0; transform: scale(1.04) translateX(2%); }
        .hc-slide.active { opacity: 1; transform: scale(1) translateX(0); }
        @keyframes kenburns { from { transform: scale(1) translateX(0); } to { transform: scale(1.05) translateX(-1%); } }
        .hc-slide.active { animation: kenburns ${AUTOPLAY_MS}ms ease-out forwards; }
        .hc-overlay { position: absolute; inset: 0; background: ${OVERLAY[overlayStrength]}; pointer-events: none; }
        .hc-overlay-side { position: absolute; inset: 0; background: linear-gradient(to right, rgba(0,0,0,0.6) 0%, transparent 60%); pointer-events: none; }
        .hc-content { position: absolute; inset: 0; display: flex; align-items: flex-end; padding: 0 48px 48px; max-width: 1440px; margin: 0 auto; left: 0; right: 0; }
        .hc-text-block { max-width: 620px; animation: slideUp 0.55s ease forwards; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
        .hc-channel-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: var(--hc-primary); border-radius: 100px; font-size: 11px; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: #fff; margin-bottom: 14px; }
        .hc-channel-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(255,255,255,0.7); }
        .hc-title { font-family: var(--hc-font-display); font-size: clamp(32px, 5vw, 60px); font-weight: 800; line-height: 1.05; color: #ffffff; margin: 0 0 14px; letter-spacing: -0.02em; text-shadow: 0 2px 20px rgba(0,0,0,0.4); }
        .hc-desc { font-size: clamp(14px, 1.5vw, 16px); line-height: 1.6; color: rgba(255,255,255,0.82); margin: 0 0 20px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .hc-meta { display: flex; align-items: center; gap: 20px; margin-bottom: 24px; flex-wrap: wrap; }
        .hc-meta-item { display: flex; align-items: center; gap: 5px; font-size: 13px; color: rgba(255,255,255,0.7); font-weight: 500; }
        .hc-meta-item svg { width: 14px; height: 14px; flex-shrink: 0; }
        .hc-cta-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .hc-btn-watch { display: inline-flex; align-items: center; gap: 8px; padding: 12px 28px; background: var(--hc-btn-bg); color: var(--hc-btn-text); font-family: var(--hc-font-body); font-size: 14px; font-weight: 700; border-radius: 8px; border: none; cursor: pointer; transition: transform 0.18s, box-shadow 0.18s, filter 0.18s; box-shadow: 0 4px 18px rgba(0,0,0,0.35); letter-spacing: 0.01em; }
        .hc-btn-watch:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(0,0,0,0.45); filter: brightness(1.06); }
        .hc-btn-watch svg { width: 16px; height: 16px; }
        .hc-btn-info { display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: rgba(255,255,255,0.12); color: #fff; font-family: var(--hc-font-body); font-size: 14px; font-weight: 600; border-radius: 8px; border: 1px solid rgba(255,255,255,0.2); cursor: pointer; transition: background 0.18s; backdrop-filter: blur(8px); }
        .hc-btn-info:hover { background: rgba(255,255,255,0.2); }
        .hc-arrow { position: absolute; top: 50%; transform: translateY(-50%); width: 48px; height: 48px; border-radius: 50%; background: rgba(0,0,0,0.38); border: 1.5px solid rgba(255,255,255,0.2); color: #ffffff; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.18s, border-color 0.18s, transform 0.18s; z-index: 10; backdrop-filter: blur(6px); }
        .hc-arrow:hover { background: rgba(0,0,0,0.62); border-color: rgba(255,255,255,0.5); transform: translateY(-50%) scale(1.08); }
        .hc-arrow svg { width: 20px; height: 20px; }
        .hc-arrow-left { left: 20px; }
        .hc-arrow-right { right: 20px; }
        .hc-dots { position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 8px; z-index: 10; }
        .hc-dot { position: relative; height: 3px; border-radius: 3px; background: rgba(255,255,255,0.35); cursor: pointer; transition: width 0.3s ease, background 0.3s; overflow: hidden; border: none; padding: 0; }
        .hc-dot.inactive { width: 20px; }
        .hc-dot.active { width: 44px; background: rgba(255,255,255,0.4); }
        .hc-dot-fill { position: absolute; left: 0; top: 0; bottom: 0; background: #ffffff; border-radius: 3px; transition: width 0s linear; }
        .hc-counter { position: absolute; top: 20px; right: 24px; font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.55); letter-spacing: 0.08em; z-index: 10; font-family: var(--hc-font-body); }
        @media (max-width: 768px) { .hc-content { padding: 0 20px 36px; } .hc-arrow-left { left: 10px; } .hc-arrow-right { right: 10px; } .hc-btn-info { display: none; } }
        @media (max-width: 480px) { .hc-title { font-size: 28px; } .hc-desc { display: none; } }
      `}</style>

            <section
                className="hc-root hc-wrap"
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
                aria-label={`Hero carousel — slide ${currentIndex + 1} of ${content.length}`}
            >
                {content.map((item, i) => (
                    <div
                        key={item.id}
                        className={`hc-slide ${i === currentIndex ? 'active' : 'entering-next'}`}
                        style={{ backgroundImage: `url(${item.thumbnail})` }}
                        aria-hidden={i !== currentIndex}
                    />
                ))}

                <div className="hc-overlay" />
                {isCinematic && <div className="hc-overlay-side" />}

                {content.length > 1 && (
                    <div className="hc-counter" aria-hidden>
                        {String(currentIndex + 1).padStart(2, '0')} / {String(content.length).padStart(2, '0')}
                    </div>
                )}

                <div className="hc-content">
                    <div className="hc-text-block" key={`text-${currentIndex}`}>
                        {current.channelName && (
                            <div className="hc-channel-pill"><span className="hc-channel-dot" />{current.channelName}</div>
                        )}
                        <h2 className="hc-title">{current.title}</h2>
                        {current.description && <p className="hc-desc">{current.description}</p>}
                        <div className="hc-meta">
                            {Boolean(settings?.showDuration && current.duration) && (
                                <span className="hc-meta-item">
                                    <svg viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                    </svg>
                                    {current.duration}
                                </span>
                            )}
                            {Boolean(settings?.showViewCount && current.views) && (
                                <span className="hc-meta-item">
                                    <svg viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                    </svg>
                                    {formatViews(current.views)} views
                                </span>
                            )}
                        </div>
                        <div className="hc-cta-row">
                            <button className="hc-btn-watch" onClick={() => onVideoClick?.(current.id)} aria-label={`Watch ${current.title}`}>
                                <svg viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                </svg>
                                Watch Now
                            </button>
                            <button className="hc-btn-info" aria-label="More info">
                                <svg viewBox="0 0 20 20" fill="currentColor" style={{ width: 15, height: 15 }}>
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                More Info
                            </button>
                        </div>
                    </div>
                </div>

                {content.length > 1 && (
                    <>
                        <button className="hc-arrow hc-arrow-left" onClick={() => goTo('prev')} aria-label="Previous slide">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button className="hc-arrow hc-arrow-right" onClick={() => goTo('next')} aria-label="Next slide">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {content.length > 1 && (
                    <div className="hc-dots" role="tablist" aria-label="Slides">
                        {content.map((_, i) => (
                            <button
                                key={i}
                                role="tab"
                                aria-selected={i === currentIndex}
                                aria-label={`Go to slide ${i + 1}`}
                                className={`hc-dot ${i === currentIndex ? 'active' : 'inactive'}`}
                                onClick={() => goTo(i)}
                            >
                                {i === currentIndex && (
                                    <span className="hc-dot-fill" style={{ width: `${progress}%`, transition: `width ${PROGRESS_TICK}ms linear` }} />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </section>
        </>
    );
};

export default HeroCarousel;